import {
  checkBrandContrast,
  checkBrandDesign,
  normalizeHexColor,
  type BrandColors,
  type ContrastCheck,
} from '@eklevo/shared/brand'
import type { CompanyLegalField } from '@eklevo/shared/company'
import { TIME_ZONES, type TimeZone } from '@eklevo/shared/domain'
import { LOCALES, type BackOfficeMessageKey, type Locale } from '@eklevo/shared/i18n/backoffice'
import { UPLOAD_RULES } from '@eklevo/shared/uploads'
import { AlertTriangle, Check, ImageIcon, X } from 'lucide-react'
import { useId } from 'react'
import { Button } from '../../../components/ui/button'
import { Checkbox } from '../../../components/ui/checkbox'
import { Field } from '../../../components/ui/field'
import { Select } from '../../../components/ui/select'
import { TextInput } from '../../../components/ui/text-input'
import { FileButton, UploadStatus, type UploadTexts } from '../../../components/ui/upload'
import type { UploadState } from '../../../lib/uploads'
import {
  INPUT_BOUNDS,
  logoError,
  type FieldErrors,
  type LegalDraft,
  type NoticeDraft,
  type WizardDraft,
} from '../../lib/company-wizard'
import { useI18n } from '../../lib/i18n'
import { useFieldError, type StepProps } from './step'

/*
 * Step 1, «Datos de la empresa» (ADR 0009 §7; PRD 04 §2.3 without the sector and the size): the
 * name, the default language, the time zone (Europe/Madrid unless the company is in the Canary
 * Islands), its legal data, all optional (decided on 2026-10-18), and, if wanted, the brand
 * colours, with the contrast of each use of them as the operator tool measures it (ADR 0007 §5):
 * a rule that fails stops the step, a design check only warns. The logo (ADR 0007), if wanted, is
 * uploaded from the browser as soon as it is chosen (ADR 0009 §6): PNG or JPEG, up to 1 MB and 32
 * to 4,000 pixels a side, analysed before it is the draft's; the server checks it again on «Crear
 * empresa».
 */
export function CompanyStep({ draft, errors, change, logo }: StepProps & { logo: LogoUpload }) {
  const { t } = useI18n()
  const error = useFieldError(errors)
  return (
    <div className="space-y-8">
      <p className="text-sm text-ink-muted">{t('wizard.company.intro')}</p>
      <div className="grid gap-4 md:grid-cols-2">
        <Field
          label={t('wizard.company.name')}
          required
          hint={t('wizard.company.name.hint')}
          error={error('name')}
          className="md:col-span-2"
        >
          <TextInput
            value={draft.name}
            maxLength={INPUT_BOUNDS.line}
            autoComplete="off"
            onChange={(e) => {
              const name = e.target.value
              change((d) => ({ ...d, name }), ['name'])
            }}
          />
        </Field>
        <Field
          label={t('wizard.company.locale')}
          required
          hint={t('wizard.company.locale.hint')}
          error={error('defaultLocale')}
        >
          <Select
            value={draft.defaultLocale}
            onChange={(e) => {
              const defaultLocale = e.target.value as Locale
              // The notice of the new language becomes the required one; the old language's,
              // if written, stays as one more, and if not, goes with its problems.
              const was = draft.defaultLocale
              const kept = noticeKept(draft.notice[was])
              change(
                (d) => {
                  const old = d.notice[d.defaultLocale]
                  return {
                    ...d,
                    defaultLocale,
                    notice: {
                      ...d.notice,
                      [d.defaultLocale]: { ...old, enabled: noticeKept(old) },
                    },
                  }
                },
                [
                  'defaultLocale',
                  'notice',
                  ...(kept ? [] : [`notice.${was}.url`, `notice.${was}.body`]),
                ],
              )
            }}
          >
            {LOCALES.map((l) => (
              <option key={l} value={l}>
                {t(`wizard.locale.${l}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label={t('wizard.company.timeZone')}
          required
          hint={t('wizard.company.timeZone.hint')}
          error={error('timeZone')}
        >
          <Select
            value={draft.timeZone}
            onChange={(e) => {
              const timeZone = e.target.value as TimeZone
              change((d) => ({ ...d, timeZone }), ['timeZone'])
            }}
          >
            {TIME_ZONES.map((zone) => (
              <option key={zone} value={zone}>
                {t(`wizard.timeZone.${zone}`)}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <LegalFields draft={draft} errors={errors} change={change} />

      <LogoField draft={draft} error={error('logo')} logo={logo} />

      <BrandFields draft={draft} errors={errors} change={change} />
    </div>
  )
}

/** How each legal field is laid out and typed in. */
const LEGAL_LAYOUT: { field: CompanyLegalField; wide?: true; email?: true }[] = [
  { field: 'legalName', wide: true },
  { field: 'taxId' },
  { field: 'privacyContact', email: true },
  { field: 'address', wide: true },
]

/**
 * A company's legal data, as the wizard's first step and «Editar» (ADR 0009 §8) ask for them
 * (decided on 2026-10-18): the legal name, the tax id, the privacy contact, whose hint recommends
 * a shared mailbox, and the address, all optional.
 */
export function LegalFields<D extends LegalDraft>({
  draft,
  errors,
  change,
  hint = 'wizard.company.legal.hint',
}: {
  draft: D
  errors: FieldErrors
  change: (update: (draft: D) => D, fields: readonly string[]) => void
  /** What the block says of them all; by default, the wizard's. */
  hint?: BackOfficeMessageKey
}) {
  const { t } = useI18n()
  const error = useFieldError(errors)
  const id = useId()
  return (
    <fieldset className="min-w-0 space-y-4" aria-describedby={`${id}-hint`}>
      <legend className="text-sm font-medium">{t('wizard.company.legal')}</legend>
      <p id={`${id}-hint`} className="text-xs text-ink-muted">
        {t(hint)}
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        {LEGAL_LAYOUT.map(({ field, wide, email }) => (
          <Field
            key={field}
            label={t(`wizard.company.${field}`)}
            hint={t(`wizard.company.${field}.hint`)}
            error={error(field)}
            className={wide ? 'md:col-span-2' : undefined}
          >
            <TextInput
              value={draft[field]}
              type={email ? 'email' : 'text'}
              maxLength={INPUT_BOUNDS.line}
              autoComplete="off"
              spellCheck={field === 'legalName' || field === 'address'}
              onChange={(e) => {
                const value = e.target.value
                change((d) => ({ ...d, [field]: value }), [field])
              }}
            />
          </Field>
        ))}
      </div>
    </fieldset>
  )
}

/** The page's logo upload: where it stands, and what the field does. */
export interface LogoUpload {
  state: UploadState | undefined
  choose: (file: File) => void
  cancel: () => void
  remove: () => void
}

function LogoField({
  draft,
  error,
  logo,
}: {
  draft: WizardDraft
  error: string | null
  logo: LogoUpload
}) {
  const { t } = useI18n()
  const id = useId()
  const texts: UploadTexts = {
    sending: (file) => t('upload.sending', { file }),
    percent: (percent) => t('upload.percent', { percent: percent / 100 }),
    progress: t('upload.progress'),
    scanning: (file) => t('upload.scanning', { file }),
    cancel: t('upload.cancel'),
    cancelLabel: (file) => t('upload.cancel.label', { file }),
    failed: (reason) => t(logoError(reason)),
  }
  const ready = draft.logo?.status === 'ready' ? draft.logo : null
  return (
    <section aria-labelledby={id} className="space-y-2" data-field="logo">
      <h3 id={id} className="text-sm font-medium">
        {t('wizard.logo')}
      </h3>
      <p id={`${id}-hint`} className="text-xs text-ink-muted">
        {t('wizard.logo.hint')}
      </p>
      {ready ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-line p-3 text-sm">
          <ImageIcon aria-hidden className="size-5 shrink-0 text-ink-muted" />
          <p className="min-w-0 flex-1 [overflow-wrap:anywhere]">
            {t('wizard.logo.ready', { file: ready.file })}
          </p>
          <FileButton
            variant="secondary"
            size="sm"
            accept={UPLOAD_RULES.logo.accept}
            onFile={logo.choose}
          >
            {t('wizard.logo.change')}
          </FileButton>
          <Button variant="secondary" size="sm" onClick={logo.remove}>
            {t('wizard.logo.remove')}
          </Button>
        </div>
      ) : (
        (!logo.state || logo.state.status === 'failed') && (
          <FileButton
            variant="secondary"
            size="sm"
            accept={UPLOAD_RULES.logo.accept}
            aria-describedby={`${id}-hint`}
            aria-invalid={error ? true : undefined}
            onFile={logo.choose}
          >
            {t('wizard.logo.choose')}
          </FileButton>
        )
      )}
      <UploadStatus state={logo.state} texts={texts} onCancel={logo.cancel} />
      {error && logo.state?.status !== 'failed' && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
    </section>
  )
}

/** Whether a language's notice stays once it is no longer the company's: added, or written. */
function noticeKept(notice: NoticeDraft): boolean {
  return notice.enabled || notice.url.trim() !== '' || notice.body.trim() !== ''
}

const COLOR_FIELDS = ['brand.primary', 'brand.accent'] as const

/** A company's brand colours as a form holds them: used or not, and each as typed. */
export interface BrandDraft {
  enabled: boolean
  primary: string
  accent: string
}

/**
 * The brand colours of a company, as the wizard's first step and «Editar» (ADR 0009 §8) ask for
 * them: whether to use them, each colour, and the contrast of each use of them (ADR 0007 §5).
 */
export function BrandFields<D extends { brand: BrandDraft }>({
  draft,
  errors,
  change,
}: {
  draft: D
  errors: FieldErrors
  change: (update: (draft: D) => D, fields: readonly string[]) => void
}) {
  const { t } = useI18n()
  const brand = draft.brand
  const primary = normalizeHexColor(brand.primary)
  const accent = normalizeHexColor(brand.accent)
  const setColor = (which: 'primary' | 'accent', value: string) =>
    // The rules are about the pair: either colour may fix the other's problem.
    change((d) => ({ ...d, brand: { ...d.brand, [which]: value } }), COLOR_FIELDS)
  return (
    <fieldset className="min-w-0 space-y-4">
      <legend className="mb-4 text-sm font-medium">{t('wizard.brand')}</legend>
      <Checkbox
        label={t('wizard.brand.use')}
        hint={t('wizard.brand.use.hint')}
        checked={brand.enabled}
        onChange={(e) => {
          const enabled = e.target.checked
          change((d) => ({ ...d, brand: { ...d.brand, enabled } }), COLOR_FIELDS)
        }}
      />
      {brand.enabled && (
        <>
          <div className="grid gap-4 md:grid-cols-2">
            <ColorField
              which="primary"
              value={brand.primary}
              errors={errors}
              onChange={(v) => setColor('primary', v)}
            />
            <ColorField
              which="accent"
              value={brand.accent}
              errors={errors}
              onChange={(v) => setColor('accent', v)}
            />
          </div>
          {primary && accent && <ContrastFeedback colors={{ primary, accent }} />}
        </>
      )}
    </fieldset>
  )
}

function ColorField({
  which,
  value,
  errors,
  onChange,
}: {
  which: 'primary' | 'accent'
  value: string
  errors: FieldErrors
  onChange: (value: string) => void
}) {
  const { t } = useI18n()
  const error = useFieldError(errors)
  return (
    <Field
      label={t(`wizard.brand.${which}`)}
      required
      hint={t('wizard.brand.hint')}
      error={error(`brand.${which}`)}
    >
      <div className="flex items-center gap-2">
        <input
          type="color"
          aria-label={t(`wizard.brand.pick.${which}`)}
          value={normalizeHexColor(value) ?? '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-12 shrink-0 cursor-pointer rounded-lg border border-line bg-surface p-1"
        />
        <TextInput
          value={value}
          maxLength={INPUT_BOUNDS.color}
          spellCheck={false}
          autoComplete="off"
          className="font-mono"
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </Field>
  )
}

/** A contrast ratio as the page shows it: never rounded up past a minimum. */
function ratioText(ratio: number, locale: string, digits: number): string {
  const floored = Math.floor(ratio * 100) / 100
  return `${new Intl.NumberFormat(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: 2,
  }).format(floored)}:1`
}

/**
 * Each rule of ADR 0007 §5 for the pair, passed or not, and each design check, passed or a
 * warning, with the ratio measured and the minimum. Read out as it changes would be too much:
 * the colour's field says when a rule fails.
 */
function ContrastFeedback({ colors }: { colors: BrandColors }) {
  const { t, locale } = useI18n()
  const id = useId()
  const line = (c: ContrastCheck<string>, design: boolean) => {
    const values = {
      ratio: ratioText(c.ratio, locale, 2),
      minimum: ratioText(c.minimum, locale, 0),
    }
    const tone = c.passed ? 'passed' : design ? 'warning' : 'failed'
    const Icon = tone === 'passed' ? Check : tone === 'warning' ? AlertTriangle : X
    return (
      <li key={c.rule} className="flex items-start gap-2" data-contrast={c.rule}>
        <Icon
          aria-hidden
          className={
            tone === 'passed'
              ? 'mt-0.5 size-4 shrink-0 text-success'
              : tone === 'warning'
                ? 'mt-0.5 size-4 shrink-0 text-ink'
                : 'mt-0.5 size-4 shrink-0 text-danger'
          }
        />
        <span className="min-w-0">
          <span className="font-medium">{t(`wizard.brand.rule.${c.rule as ContrastRule}`)}</span>
          {': '}
          <span className={tone === 'failed' ? 'text-danger' : 'text-ink-muted'}>
            {t(`wizard.brand.check.${tone}`, values)}
          </span>
        </span>
      </li>
    )
  }
  return (
    <section aria-labelledby={id} className="rounded-lg border border-line p-4">
      <h4 id={id} className="text-sm font-medium">
        {t('wizard.brand.contrast')}
      </h4>
      <p className="mt-1 text-xs text-ink-muted">{t('wizard.brand.contrast.intro')}</p>
      <ul className="mt-3 space-y-2 text-sm">
        {checkBrandContrast(colors).map((c) => line(c, false))}
        {checkBrandDesign(colors).map((c) => line(c, true))}
      </ul>
    </section>
  )
}

type ContrastRule =
  | ReturnType<typeof checkBrandContrast>[number]['rule']
  | ReturnType<typeof checkBrandDesign>[number]['rule']
